import React, { useEffect, useState, useRef, useMemo } from "react";
import { fetchWrapper } from "../utils/fetchWrapper";
import { findProperty } from "../api/project";
import ProjectCard from "./ProjectCard";
import { toast } from "react-hot-toast";
import ConfirmModal from "./ui/ConfirmModal";
import { useSelector } from "react-redux";
import { useWebSocketData } from "../context/WebSocketContext";
import { formatDate, toCamelCase, getSSLStatus, capitalize } from "../utils/units";
import SearchableSelect from "./ui/SearchableSelect";
import { MdFolder, MdCheckCircle, MdAdd, MdSearch } from "react-icons/md";
import "./ui/SearchableSelect.css";
import "./Projects.css";

// import { bootstrap } from "bootstrap";

function Projects() {
  const { permissions } = useSelector((state) => state.auth);
  const isViewOnly = permissions?.ProjectsViewOnly === true;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [urbanProjects, setUrbanProjects] = useState([]);

  // 🔍 Filter State
  const [filters, setFilters] = useState({
    city: "",
    accountStatus: "",
    healthStatus: "",
    sslStatus: ""
  });

  const [formData, setFormData] = useState({
    domain: "",
    projectName: "",
    projectId: "",
    city: "",
    status: "inactive",
    templateRepo: "", // template repo URL
  });
  const [templatePreview, setTemplatePreview] = useState(null);

  const { live } = useWebSocketData();
  const isConnected = live && live.length > 0;

  const timeRef = useRef();

  // 🧪 Data Enrichment: Combine DB data with Live Status
  const enrichedProjects = useMemo(() => {
    return projects.map(p => {
      const statusData = live.find(l => l.domain === p.domain) || {};
      const sslStatus = getSSLStatus(statusData?.ssl?.sslRemainingDays);
      
      return {
        ...p,
        liveStatus: statusData.status || 'unknown',
        isHealthy: statusData.isHealthy || false,
        sslStatus: sslStatus
      };
    });
  }, [projects, live]);

  // 🏙️ Extract Unique Normalized Cities with Counts
  const cities = useMemo(() => {
    const counts = {};
    projects.forEach(p => {
      const city = p.city?.trim();
      if (city) {
        const lower = city.toLowerCase();
        counts[lower] = (counts[lower] || 0) + 1;
      }
    });

    return Object.keys(counts).sort().map(city => ({
      name: city,
      count: counts[city]
    }));
  }, [projects]);

  // 📊 Calculate counts for other filters
  const filterCounts = useMemo(() => {
    const counts = {
      account: { active: 0, inactive: 0 },
      health: { live: 0, warning: 0, down: 0, unknown: 0 },
      ssl: { valid: 0, expiring: 0, critical: 0, expired: 0, unknown: 0 }
    };

    enrichedProjects.forEach(p => {
      // Account Status
      if (p.status === 'active') counts.account.active++;
      else if (p.status === 'inactive') counts.account.inactive++;

      // Health Status
      if (p.isHealthy) counts.health.live++;
      else if (p.liveStatus === 'down') counts.health.down++;
      else if (p.liveStatus === 'live' && !p.isHealthy) counts.health.warning++;
      else counts.health.unknown++;

      // SSL Status
      if (p.sslStatus && counts.ssl.hasOwnProperty(p.sslStatus)) {
        counts.ssl[p.sslStatus]++;
      } else {
        counts.ssl.unknown++;
      }
    });

    return counts;
  }, [enrichedProjects]);

  // 🎯 Final Filtered List
  const filteredProjects = useMemo(() => {
    return enrichedProjects.filter(p => {
      // 1. Search Query
      const matchesSearch = (p.projectName || p.domain || "").toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      // 2. City Filter
      if (filters.city && p.city?.trim().toLowerCase() !== filters.city) return false;

      // 3. Account Status
      if (filters.accountStatus && p.status !== filters.accountStatus) return false;

      // 4. Live Health Status
      if (filters.healthStatus) {
        if (filters.healthStatus === 'live' && !p.isHealthy) return false;
        if (filters.healthStatus === 'down' && p.liveStatus !== 'down') return false;
        if (filters.healthStatus === 'warning' && (p.liveStatus !== 'live' || p.isHealthy)) return false;
        if (filters.healthStatus === 'unknown' && (p.isHealthy || p.liveStatus === 'down' || (p.liveStatus === 'live' && !p.isHealthy))) return false;
      }

      // 5. SSL Status
      if (filters.sslStatus && p.sslStatus !== filters.sslStatus) return false;

      return true;
    });
  }, [enrichedProjects, searchQuery, filters]);

  const clearFilters = () => {
    setFilters({
      city: "",
      accountStatus: "",
      healthStatus: "",
      sslStatus: ""
    });
    setSearchQuery("");
  };

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await fetchWrapper.get("/projects");
      setProjects(res.data || []);
    } catch (err) {
      console.error("Failed to fetch projects", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProjects();
  }, []);

  // Search for existing projects
  useEffect(() => {
    if (!formData.projectName) {
      setUrbanProjects([]);
      return;
    }

    clearTimeout(timeRef.current);

    timeRef.current = setTimeout(async () => {
      try {
        const response = await findProperty({
          searchValue: formData.projectName,
        });
        setUrbanProjects(response.propertys || []);
      } catch (err) {
        console.error("Error fetching urban projects", err);
        setUrbanProjects([]);
      }
    }, 300);

    return () => clearTimeout(timeRef.current);
  }, [formData.projectName]);

  // Preview template site when templateRepo changes

  /**
   * Sanitize domain to use as GitHub repo name
   */
  function sanitizeRepoName(domain) {
    return domain.toLowerCase().replace(/[^a-z0-9-]/g, "-");
  }
  // Inside Projects component...
  useEffect(() => {
    if (!formData.templateRepo && !formData.domain) {
      setTemplatePreview(null);
      return;
    }

    let previewUrl = null;

    // If the project already exists (domain provided), preview live site
    if (formData.domain) {
      previewUrl = `http://${sanitizeRepoName(formData.domain)}`;
    }
    // Otherwise, fallback to GitHub raw repo index.html
    else if (formData.templateRepo.includes("github.com")) {
      const repoUrl = formData.templateRepo
        .replace("https://github.com/", "")
        .replace(".git", "");
      previewUrl = `https://raw.githubusercontent.com/${repoUrl}/main/index.html`;
    }

    setTemplatePreview(previewUrl);
  }, [formData.templateRepo, formData.domain]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await fetchWrapper.post("/projects", formData);
      setFormData({
        domain: "",
        projectName: "",
        projectId: "",
        city: "",
        status: "inactive",
        templateRepo: "",
      });

      setTemplatePreview(null);
      loadProjects();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error("A project with this domain already exists.");
      } else {
        toast.error(err?.response?.data?.message || "Failed to save project.");
      }
    }
  };
  const confirmDelete = (project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!projectToDelete) return;
    try {
      await fetchWrapper.delete(`/projects/${projectToDelete._id}`);
      toast.success("Project deleted");
      setShowDeleteModal(false);
      setProjectToDelete(null);
      loadProjects();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete project");
    }
  };

  return (
    <div className="projects-container">
      <div className="projects-header">
        <div>
          <h3 className="projects-title">Projects</h3>
          <p className="projects-subtitle">Manage your microsite projects</p>
        </div>
        <div className="projects-stats">
          <span className="stat-badge">
            <MdFolder size={18} /> {projects.length} Total
          </span>
          <span className="stat-badge">
            <MdCheckCircle size={18} style={{ color: "var(--accent-green)" }} />{" "}
            {projects.filter((p) => p.status === "active").length} Active
          </span>
        </div>
      </div>

      {/* Create / Add Project Form */}
      {!isViewOnly && (
        <div className="project-form-card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5>Add New Project</h5>
            <button
              type="submit"
              form="project-form"
              className="btn btn-dark px-4"
              style={{ borderRadius: "8px", fontWeight: 600 }}
            >
              <MdAdd size={20} /> Add Project
            </button>
          </div>
          <div className="card-body">
            <form id="project-form" onSubmit={handleSave}>
              <div className="row">
                {/* Domain */}
                <div className="col-lg-3 col-md-4 mb-3">
                  <label className="form-label">Domain *</label>
                  <input
                    type="text"
                    name="domain"
                    className="form-control"
                    placeholder="example.com"
                    value={formData.domain}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Project Name */}
                <div className="col-lg-3 col-md-4 mb-3 position-relative">
                  <label className="form-label">Project Name *</label>
                  <input
                    type="text"
                    name="projectName"
                    className="form-control"
                    placeholder="My Project"
                    value={formData.projectName}
                    onChange={handleChange}
                    required
                  />
                  {urbanProjects.length > 0 && (
                    <div
                      className="position-absolute d-flex flex-column overflow-y-auto bg-white z-3 w-100 border border-1 rounded-2 shadow"
                      style={{ maxHeight: 200, top: "100%" }}
                    >
                      {urbanProjects.map((e) => (
                        <div
                          key={e._id}
                          className="p-2 border-bottom border-light-subtle onHover"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              projectId: e._id,
                              projectName: e.name,
                            });
                            setUrbanProjects([]);
                          }}
                        >
                          {e.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* City */}
                <div className="col-lg-2 col-md-4 mb-3">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    className="form-control"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>

                {/* Status Dropdown */}
                <div className="col-lg-2 col-md-4 mb-3">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Template selection */}
                <div className="col-lg-2 col-md-4 mb-3">
                  <label className="form-label">Template</label>
                  <SearchableSelect
                    value={formData.templateRepo}
                    onChange={(val) => setFormData(prev => ({ ...prev, templateRepo: val }))}
                    options={[
                      { value: "", label: "Default Template" },
                      { value: "sample-project", label: "Sample Project" },
                      ...projects
                        .filter(p => p.githubRepo && p._id !== formData.projectId)
                        .map(p => ({ value: p.domain, label: p.projectName || p.domain }))
                    ]}
                    placeholder="Default Template"
                    searchPlaceholder="Search templates..."
                  />
                </div>
              </div>

              {/* Template Preview */}
              {templatePreview && (
                <div className="mt-3 p-3 border border-light rounded-3 bg-light">
                  <strong>Template Preview:</strong>
                  <iframe
                    src={templatePreview}
                    title="Template Preview"
                    style={{
                      width: "100%",
                      height: 400,
                      border: "1px solid #ddd",
                      marginTop: 12,
                      borderRadius: 8,
                    }}
                  />
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-5 text-muted">No projects found. Add your first project above!</div>
      ) : (
        <div className="row m-0">
          {/* Search & Filter Bar */}
          <div className="col-12 mb-4 px-0">
            <div className="filter-card p-3 bg-white shadow-sm rounded-3">
              <div className="row g-2 align-items-center">
                <div className="col-lg-4">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted">
                      <MdSearch size={18} />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search projects..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-lg-2 col-md-3">
                  <select name="city" className="form-select border-light-subtle" value={filters.city} onChange={handleFilterChange}>
                    <option value="">All Cities ({projects.length})</option>
                    {cities.map(c => (
                      <option key={c.name} value={c.name}>
                        {capitalize(c.name)} ({c.count})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-2 col-md-3">
                  <select name="accountStatus" className="form-select border-light-subtle" value={filters.accountStatus} onChange={handleFilterChange}>
                    <option value="">Account: All ({projects.length})</option>
                    <option value="active">Active ({filterCounts.account.active})</option>
                    <option value="inactive">Inactive ({filterCounts.account.inactive})</option>
                  </select>
                </div>
                <div className="col-lg-2 col-md-3">
                  <select 
                    name="healthStatus" 
                    className="form-select border-light-subtle" 
                    value={filters.healthStatus} 
                    onChange={handleFilterChange}
                    disabled={!isConnected}
                    title={!isConnected ? "Live data unavailable" : ""}
                  >
                    <option value="">Health: All ({projects.length})</option>
                    <option value="live">Live/Healthy ({filterCounts.health.live})</option>
                    <option value="warning">Warning/Issues ({filterCounts.health.warning})</option>
                    <option value="down">Down/Offline ({filterCounts.health.down})</option>
                    {filterCounts.health.unknown > 0 && <option value="unknown">Pending/Unknown ({filterCounts.health.unknown})</option>}
                  </select>
                </div>
                <div className="col-lg-2 col-md-3">
                  <select 
                    name="sslStatus" 
                    className="form-select border-light-subtle" 
                    value={filters.sslStatus} 
                    onChange={handleFilterChange}
                    disabled={!isConnected}
                    title={!isConnected ? "SSL data unavailable" : ""}
                  >
                    <option value="">SSL: All ({projects.length})</option>
                    <option value="valid">Valid ({filterCounts.ssl.valid})</option>
                    <option value="expiring">Expiring Soon ({filterCounts.ssl.expiring})</option>
                    <option value="critical">Critical ({filterCounts.ssl.critical})</option>
                    <option value="expired">Expired ({filterCounts.ssl.expired})</option>
                    {filterCounts.ssl.unknown > 0 && <option value="unknown">Pending/Unknown ({filterCounts.ssl.unknown})</option>}
                  </select>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top border-light">
                <div className="results-info text-muted small">
                  Showing <strong>{filteredProjects.length}</strong> of <strong>{projects.length}</strong> projects
                  {searchQuery || Object.values(filters).some(v => v) ? (
                    <span className="ms-2 badge bg-primary-subtle text-primary border border-primary-subtle">Filtered</span>
                  ) : null}
                </div>
                <button className="btn btn-sm btn-link text-decoration-none text-muted" onClick={clearFilters}>
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>

          {filteredProjects.map((project) => (
            <ProjectCard
              key={project._id}
              project={project}
              onDelete={confirmDelete}
            />
          ))}
        </div>
      )}
      <ConfirmModal
        show={showDeleteModal}
        title="Delete Project"
        message={`Are you sure you want to delete ${projectToDelete?.projectName}?`}
        detail={`This will permanently remove the project and its configuration. This action cannot be undone.`}
        confirmText="Delete Project"
        cancelText="Cancel"
        variant="danger"
        confirmPhrase="confirm"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}

export default Projects;
